import React, { useState, useEffect } from 'react';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import PublicTokenIcon from './PublicTokenIcon';
import ApexChart from 'react-apexcharts';
import { getPublicChart, calculate24hChange } from '../services/publicChartService';
import { isRTL } from '../i18n';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import publicApi from '../services/publicApi';

const MarketOverviewTable = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { navigateWithLang } = useLanguageNavigation();
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch public coins data
  useEffect(() => {
    const fetchCoins = async () => {
      try {
        const response = await publicApi.get('/api/public/coins');
        setCoins(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching public coins:', err);
        setError('Failed to load market data');
      } finally {
        setLoading(false);
      }
    };

    fetchCoins();
  }, []);

  const [chartData, setChartData] = useState({});
  const [chartLoading, setChartLoading] = useState({});

  // Load chart data for coins
  useEffect(() => {
    const loadChartData = async () => {
      if (!coins || coins.length === 0) return;
      
      // Load chart data for each coin
      for (const coin of coins) {
        if (!chartData[coin.symbol] && !chartLoading[coin.symbol]) {
          setChartLoading(prev => ({ ...prev, [coin.symbol]: true }));
          
          try {
            const data = await getPublicChart(coin.symbol, '24h', 24);
            setChartData(prev => ({ ...prev, [coin.symbol]: data }));
          } catch (error) {
            console.error(`Failed to load chart data for ${coin.symbol}:`, error);
          } finally {
            setChartLoading(prev => ({ ...prev, [coin.symbol]: false }));
          }
        }
      }
    };

    loadChartData();
  }, [coins, chartData, chartLoading]);

  const handleRowClick = (coinSymbol) => {
    navigateWithLang(`coin/${coinSymbol}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!coins || coins.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          {t('common.noDataAvailable')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 1 } }}>


      <Box display="flex" flexDirection="column" gap={0} sx={{ bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
        {coins.map((coin, index) => {
          const currentPrice = parseFloat(coin.current_price) || 0;
          const coinChartData = chartData[coin.symbol];
          
          // Calculate 24h change from chart data
          const gain24hPercent = coinChartData ? calculate24hChange(coinChartData) : 0;

          return (
            <Box 
              key={coin.symbol + '-' + index} 
              onClick={() => handleRowClick(coin.symbol)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                py: { xs: 0.5, sm: 1.5 }, 
                px: { xs: 0.5, sm: 2 }, 
                borderBottom: index < coins.length - 1 ? '1px solid #f0f0f0' : 'none',
                width: '100%', 
                cursor: 'pointer', 
                '&:hover': { background: '#fafbfc' },
                '&:last-child': { borderBottom: 'none' }
              }}
            >
              {/* Coin icon and info */}
              <Box display="flex" alignItems="center" minWidth={60} flex={2}>
                <PublicTokenIcon coinSymbol={coin.symbol} size={22} sx={{ mx: 0.75 }} />
                <Box sx={{ position: 'relative', mr: isRTL() ? 2 : 0, ml: isRTL() ? 0 : 2 }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.1rem' }, lineHeight: 1.2 }}>
                      {coin.symbol}
                    </Typography>
                  </Box>
                  {coin.name && (
                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.2, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                      {coin.name}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Sparkline */}
              <Box flex={2} minWidth={40} maxWidth={70} sx={{ mx: { xs: 0.5, sm: 1 } }}>
                {(() => {
                  try {
                    if (!coinChartData || !Array.isArray(coinChartData.data)) {
                      return <Box sx={{ width: 60, height: 35, bgcolor: '#f5f5f5', borderRadius: 1 }} />;
                    }

                    const closes = coinChartData.data
                      .map((d, index) => {
                        if (!d || typeof d !== 'object') return null;
                        if (typeof d.close !== 'number' || !isFinite(d.close)) return null;
                        return d.close;
                      })
                      .filter(v => v !== null && typeof v === 'number' && isFinite(v));

                    if (closes.length < 2) {
                      return <Box sx={{ width: 60, height: 35, bgcolor: '#f5f5f5', borderRadius: 1 }} />;
                    }

                    const minValue = Math.min(...closes);
                    const maxValue = Math.max(...closes);
                    if (minValue <= 0 || maxValue <= 0 || !isFinite(minValue) || !isFinite(maxValue)) {
                      return <Box sx={{ width: 60, height: 35, bgcolor: '#f5f5f5', borderRadius: 1 }} />;
                    }

                    const finalData = closes.map(val => {
                      if (typeof val !== 'number' || !isFinite(val) || val <= 0) return null;
                      return val;
                    }).filter(v => v !== null);

                    if (finalData.length < 2) {
                      return <Box sx={{ width: 60, height: 35, bgcolor: '#f5f5f5', borderRadius: 1 }} />;
                    }

                    const sparklineData = [...finalData];
                    
                    if (typeof currentPrice === 'number' && isFinite(currentPrice) && currentPrice > 0) {
                      const lastChartPrice = finalData[finalData.length - 1];
                      const priceDiff = Math.abs(currentPrice - lastChartPrice) / lastChartPrice;
                      if (priceDiff > 0.001) {
                        sparklineData.push(currentPrice);
                      }
                    }

                    const firstValue = sparklineData[0];
                    const lastValue = sparklineData[sparklineData.length - 1];
                    const trendColor = lastValue >= firstValue ? '#21b26f' : '#d32f2f';
                    const annotations = firstValue ? {
                      yaxis: [
                        {
                          y: firstValue,
                          borderColor: '#888',
                          strokeDashArray: 2,
                          label: { show: false }
                        }
                      ]
                    } : {};

                    return (
                      <ApexChart
                        type="area"
                        height={35}
                        width={60}
                        series={[
                          {
                            data: sparklineData,
                            color: trendColor,
                          }
                        ]}
                        options={{
                          chart: { 
                            sparkline: { enabled: true },
                            animations: { enabled: false }
                          },
                          stroke: { 
                            width: 2, 
                            curve: 'straight', 
                            colors: [trendColor] 
                          },
                          fill: { 
                            type: 'gradient', 
                            gradient: { 
                              shadeIntensity: 1, 
                              opacityFrom: 0.3, 
                              opacityTo: 0, 
                              stops: [0, 100], 
                              colorStops: [] 
                            }, 
                            colors: [trendColor] 
                          },
                          colors: [trendColor],
                          markers: { size: 0 },
                          tooltip: { enabled: false },
                          grid: { show: false },
                          xaxis: { 
                            labels: { show: false }, 
                            axisBorder: { show: false }, 
                            axisTicks: { show: false } 
                          },
                          yaxis: { 
                            labels: { show: false },
                            min: Math.min(...sparklineData),
                            max: Math.max(...sparklineData)
                          },
                          annotations,
                        }}
                      />
                    );
                  } catch (error) {
                    console.error(`Error rendering sparkline for ${coin.symbol}:`, error);
                    return <Box sx={{ width: 60, height: 35, bgcolor: '#f5f5f5', borderRadius: 1 }} />;
                  }
                })()}
              </Box>

              {/* Current price */}
              <Box flex={2} minWidth={60} textAlign="center" sx={{ pr: { xs: 0.5, sm: 1 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.92rem', sm: '1rem' }, lineHeight: 1.2 }}>
                  {currentPrice >= 1
                    ? ('$' + (Number.isInteger(currentPrice)
                        ? currentPrice.toLocaleString()
                        : currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      ))
                    : ('$' + currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 }).replace(/0+$/, '').replace(/\.$/, ''))
                  }
                </Typography>
              </Box>

              {/* Percentage change chip */}
              <Box flex={1} minWidth={40} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Chip
                  label={
                    typeof gain24hPercent === 'number'
                      ? `${gain24hPercent >= 0 ? '+' : ''}${gain24hPercent.toFixed(1)}%`
                      : '+0.0%'
                  }
                  color={(gain24hPercent || 0) >= 0 ? 'success' : 'error'}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: { xs: '0.72rem', sm: '0.85rem' }, minWidth: 70, height: { xs: 22, sm: 28 }, px: { xs: 0.5, sm: 1 }, borderRadius: 0 }}
                  style={{ minWidth: 70, overflow: 'visible', textOverflow: 'unset' }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MarketOverviewTable;
